import React, { useState, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import BtnGuardar from "../../../components/buttons/BtnGuardar";
import BtnCancelar from "../../../components/buttons/BtnCancelar";
import { appFetchJson } from "../../../utils/http";
import apiRoutes from "../../../api/apiRoutes";
import EessAutocomplete from "../../../components/autocomplete/EessAutocomplete"; 
import ProductoAutocomplete from "../../../components/autocomplete/ProductoAutocomplete";
import InputNumberNoScroll from "../../../components/inputs/inputNumberNoScroll";

export default function EditarPrecioProductoEess({
  onClose = () => {},
  onUpdated = () => {},
  record = null,
}) {
  const [fecha, setFecha] = useState("");
  const [selectedEess, setSelectedEess] = useState(null); 
  const [selectedProducto, setSelectedProducto] = useState(null);
  
  const [valorPrecio, setValorPrecio] = useState("");
  //const [precioCompra, setPrecioCompra] = useState("");
  
  const [inactivar, setInactivar] = useState(false);
  const [saving, setSaving] = useState(false);

  // Evaluamos si el registro original ya está inactivo (ID 13)
  const isAlreadyInactive = record?.estado === 13 || record?.estadoId === 13;

  const isValid = selectedEess !== null && selectedProducto !== null && valorPrecio !== "" && fecha !== "";

  // --- CARGAR DATOS AL ABRIR ---
  useEffect(() => {
    if (record) {
      // Fecha
      if (record.fecha && record.fecha.includes("/")) {
        const [dia, mes, anio] = record.fecha.split("/");
        setFecha(`${anio}-${mes}-${dia}`);
      } else {
        setFecha(record.fecha || "2025-12-31");
      }
      
      setValorPrecio(record.precio || 0);
      //setPrecioCompra(record.precio2 || 0);

      // Si el registro viene inactivo, marcamos el check por defecto
      if (record.estado === 13 || record.estadoId === 13) {
        setInactivar(true);
      } else {
        setInactivar(false);
      }

      // Estación
      if (record.eessId) {
        setSelectedEess({
          id: record.eessId,
          label: record.eessNombre, 
          nombre: record.eessNombre
        });
      }

      // Producto
      if (record.productoId) {
        const textoMostrar = record.productoNombre || `Producto ${record.productoId}`;
        setSelectedProducto({
          id: record.productoId,
          codigo: textoMostrar,
          nombre: textoMostrar
        });
      }
    }
  }, [record]);

  // Cerrar con ESC
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const guardar = async () => {
    // Evitamos guardar si ya está inactivo originalmente
    if (!isValid || saving || isAlreadyInactive) return;
    setSaving(true);

    try {
      const payload = {
        id: record.id,
        eessId: selectedEess?.id,
        productoId: selectedProducto?.id, 
        fecha: fecha, 
        precio: Number(valorPrecio),
        //precio2: Number(precioCompra),
        
        // Si el check está marcado (true) -> 13 (Inactivo)
        // Si no está marcado (false)     -> 12 (Activo)
        estadoId: inactivar ? 13 : 12
      };

      await appFetchJson(apiRoutes.precioProductoEess.update, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (onUpdated) onUpdated();
      onClose();

    } catch (e) {
      console.error("Error al editar:", e);
      alert(`Error al guardar cambios: ${e.message || "Error 500"}`);
    } finally {
      setSaving(false);
    }
  };

  // --- ESTILOS AUXILIARES ---
  const disabledClass = "bg-gray-200 text-gray-600 cursor-not-allowed focus:ring-0 border-gray-300";

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header flex justify-between items-center">
          <h2 className="section-title">Editar Precio Producto por EE.SS.</h2>
          <button className="modal-close-x" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-body flex flex-col items-start">
          <p className="text-gray-600 mb-4 italic text-left w-full px-8">
            Modifique los datos para el registro seleccionado.
          </p>

          {/* Banner si el producto ya está inactivo */}
          {isAlreadyInactive && (
            <div className="w-full px-8 mb-4">
              <div className="p-3 bg-red-100 text-red-800 border border-red-300 rounded">
                Este registro se encuentra <strong>INACTIVO</strong> y no puede ser editado.
              </div>
            </div>
          )}

          {/* SECCIÓN SUPERIOR */}
          <div className="flex flex-col gap-3 mb-6 w-full px-8">
            <div className="flex items-center">
              <label className="w-32 shrink-0 text-left font-bold">EE.SS.:</label>
              <div className="w-[600px]">
                <input 
                  type="text"
                  className={`input w-full h-[38px] px-3 ${disabledClass}`}
                  value={selectedEess?.nombre || ""}
                  disabled={true}
                  readOnly
                />
              </div>
            </div>

            <div className="flex items-center">
              <label className="w-32 shrink-0 text-left font-bold">Fecha:</label>
              <div className="w-[600px]">
                <input
                  type="date"
                  className={`input w-full h-[38px] px-3 ${disabledClass}`}
                  value={fecha}
                  disabled={true} 
                  readOnly
                />
              </div>
            </div>
          </div>

          <div className="flex mb-0 w-full px-8">
            <button className="bg-green-700 text-white px-6 py-2 rounded-t-md font-medium text-sm">
              Precio por Producto
            </button>
          </div>

          {/* CONTENEDOR PRINCIPAL */}
          <div className="px-8 w-full">
            <div className={`bg-gray-100 p-8 rounded-b-md rounded-tr-md space-y-5 w-full transition-opacity ${isAlreadyInactive ? "opacity-75 grayscale" : ""}`}>
              <div className="flex items-center">
                <label className="w-32 shrink-0 text-left font-bold">Producto:</label>
                <div className="w-[600px]">
                  <input 
                    type="text"
                    className={`input w-full h-[38px] px-3 ${disabledClass}`}
                    value={selectedProducto?.nombre || ""}
                    disabled={true}
                    readOnly
                  />
                </div>
              </div>

              <div className="flex items-center mt-6">
                <label className="w-32 shrink-0 text-left font-bold">Valor Precio:</label>
                <div className="w-[600px]">
                  <InputNumberNoScroll
                    className={`input w-full h-[38px] border rounded px-3 ${isAlreadyInactive ? disabledClass : "bg-white"}`}
                    value={valorPrecio}
                    onChange={(e) => setValorPrecio(e.target.value)}
                    disabled={isAlreadyInactive}
                  />
                </div>
              </div>

              {/*<div className="flex items-center">
                <label className="w-32 shrink-0 text-left font-bold">Precio Compra:</label>
                <div className="w-[600px]">
                  <InputNumberNoScroll
                    className={`input w-full h-[38px] border rounded px-3 ${isAlreadyInactive ? disabledClass : "bg-white"}`}
                    value={precioCompra}
                    onChange={(e) => setPrecioCompra(e.target.value)}
                    disabled={isAlreadyInactive}
                  />
                </div>
              </div>*/}

              {/* CHECKBOX DE INACTIVACIÓN (Solo se muestra si NO está inactivo aún) */}
              {!isAlreadyInactive && (
                <div className="flex items-center justify-end pt-4">
                  <label className="flex items-center gap-2 cursor-pointer select-none text-green-700 font-bold text-sm">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      checked={inactivar}
                      onChange={(e) => setInactivar(e.target.checked)}
                    />
                    Marque si desea INACTIVAR el producto
                  </label>
                </div>
              )}

            </div>
          </div>
        </div>

        <div className="modal-footer">
          <div className="btn-row">
            <BtnGuardar 
              label="Actualizar" 
              isValid={isValid && !isAlreadyInactive} 
              saving={saving} 
              onClick={guardar} 
            />
            <BtnCancelar onClick={onClose} />
          </div>
        </div>
      </div>
    </div>
  );
}